import React, { createContext, useContext, useState, useEffect } from 'react';
import { PageLoader } from '@/components/ui/page-loader';
import { useLocation } from 'wouter';

interface LoadingContextType {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
  navigateTo: (path: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [prevPath, setPrevPath] = useState<string | null>(null);
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [location, navigate] = useLocation();
  
  // This effect will run on mount to stop any loading state
  // that might have persisted across page reloads
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timeout);
  }, []);
  
  // This effect monitors location changes to detect when navigation is complete
  useEffect(() => {
    if (isLoading && targetPath && location === targetPath) {
      // Navigation is complete - stop loading after a small delay 
      // to ensure content is rendered
      const timeout = setTimeout(() => {
        setIsLoading(false);
        setTargetPath(null);
      }, 300);
      
      return () => clearTimeout(timeout);
    }
    
    // Safety timeout - never keep loading for more than 5 seconds
    if (isLoading) {
      const safetyTimeout = setTimeout(() => {
        setIsLoading(false);
        setTargetPath(null);
      }, 5000);
      
      return () => clearTimeout(safetyTimeout);
    }
  }, [isLoading, location, targetPath]);

  const startLoading = () => setIsLoading(true);
  const stopLoading = () => {
    setIsLoading(false);
    setTargetPath(null);
  };
  
  // Enhanced navigation function that shows loader
  const navigateTo = (path: string) => {
    // Ensure path is absolute and clean any double slashes
    const normalizedPath = path.startsWith('/') 
      ? path 
      : `/${path}`;
    
    if (normalizedPath === window.location.pathname) return;
    
    // Save the current path before navigating
    setPrevPath(window.location.pathname);
    
    // Set the target path to monitor when navigation completes
    setTargetPath(normalizedPath);
    
    // Show the loading state
    startLoading();
    
    // Use timeout to ensure the loading state is visible
    setTimeout(() => {
      // Use the normalized path for navigation
      navigate(normalizedPath);
    }, 50);
  };

  return (
    <LoadingContext.Provider value={{ isLoading, startLoading, stopLoading, navigateTo }}>
      <PageLoader isVisible={isLoading} />
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};